import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getVendors } from '../firebase';

const CATEGORIES = [
  { id: 'all',          labelKey: 'vendors.cat_all',         icon: 'fa-th' },
  { id: 'photography',  labelKey: 'vendors.cat_photography',  icon: 'fa-camera' },
  { id: 'flowers',      labelKey: 'vendors.cat_flowers',      icon: 'fa-seedling' },
  { id: 'venues',       labelKey: 'vendors.cat_venues',       icon: 'fa-building' },
  { id: 'catering',     labelKey: 'vendors.cat_catering',     icon: 'fa-utensils' },
  { id: 'music',        labelKey: 'vendors.cat_music',        icon: 'fa-music' },
  { id: 'travel',       labelKey: 'vendors.cat_travel',       icon: 'fa-plane' },
  { id: 'registry',     labelKey: 'vendors.cat_registry',     icon: 'fa-gift' },
  { id: 'other',        labelKey: 'vendors.cat_other',         icon: 'fa-ellipsis-h' },
];

export default function Vendors() {
  const { t } = useTranslation();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    getVendors().then(data => {
      const todayStr = new Date().toLocaleDateString('en-CA'); // "YYYY-MM-DD" in local time
      const active = data.filter(v => {
        if (v.hidden) return false;
        if (v.visibleFrom && v.visibleFrom > todayStr) return false;
        if (v.visibleUntil && v.visibleUntil < todayStr) return false;
        return true;
      });
      setVendors(active);
      setLoading(false);
    });
  }, []);

  const filtered = activeCategory === 'all'
    ? vendors
    : vendors.filter(v => v.category === activeCategory);

  return (
    <div>
      {/* Header */}
      <div className="card shadow-sm border-0 mb-4" style={{ background: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))', borderRadius: '16px' }}>
        <div className="card-body p-4 text-white text-center">
          <i className="fas fa-store fs-1 mb-2 d-block opacity-75"></i>
          <h3 className="fw-bold mb-1">{t('vendors.title')}</h3>
          <p className="mb-0 opacity-75">{t('vendors.subtitle')}</p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-3">
          <div className="d-flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`btn btn-sm rounded-pill px-3 ${activeCategory === cat.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                <i className={`fas ${cat.icon} me-1`}></i>
                {t(cat.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Vendor Cards */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-store fs-2 mb-2 d-block opacity-25"></i>
          <p>{t('vendors.no_vendors')}</p>
        </div>
      ) : (
        <div className="row g-3 mb-4">
          {filtered.map(vendor => (
            <div key={vendor.id} className="col-12 col-md-6 col-lg-4">
              <VendorCard vendor={vendor} t={t} categories={CATEGORIES} />
            </div>
          ))}
        </div>
      )}

      {/* Become a Partner CTA */}
      <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)' }}>
        <div className="card-body p-4 text-center">
          <i className="fas fa-handshake fs-2 text-primary mb-2 d-block"></i>
          <h5 className="fw-bold text-primary mb-1">{t('vendors.partner_cta_title')}</h5>
          <p className="text-muted mb-3">{t('vendors.partner_cta_desc')}</p>
          <button className="btn btn-primary rounded-pill px-4" onClick={() => setShowContactModal(true)}>
            <i className="fas fa-envelope me-2"></i>
            {t('vendors.partner_cta_btn')}
          </button>
        </div>
      </div>

      <p className="text-muted small text-center">
        <i className="fas fa-info-circle me-1"></i>
        {t('vendors.disclaimer')}
      </p>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} onClick={() => setShowContactModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-primary">
                  <i className="fas fa-handshake me-2"></i>{t('vendors.contact_modal_title')}
                </h5>
                <button className="btn-close" onClick={() => setShowContactModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted">{t('vendors.contact_modal_desc')}</p>
                <div className="alert alert-primary border-0 rounded-3">
                  <i className="fas fa-envelope me-2"></i>
                  <strong>{t('vendors.contact_email_label')}:</strong>{' '}
                  <a href="mailto:ptrakkides@gmail.com" className="fw-semibold">ptrakkides@gmail.com</a>
                </div>
                <ul className="list-unstyled mt-3 mb-0">
                  {['vendors.benefit_1', 'vendors.benefit_2', 'vendors.benefit_3'].map(key => (
                    <li key={key} className="mb-2 text-muted">
                      <i className="fas fa-check-circle text-success me-2"></i>{t(key)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowContactModal(false)}>
                  {t('common.cancel')}
                </button>
                <a href="mailto:ptrakkides@gmail.com" className="btn btn-primary rounded-pill px-4">
                  <i className="fas fa-paper-plane me-2"></i>{t('vendors.send_email')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VendorCard({ vendor, t, categories }) {
  const cat = categories.find(c => c.id === vendor.category);
  return (
    <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
      <div className="card-body p-4">
        <div className="d-flex align-items-center mb-3">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
            style={{ width: 48, height: 48, background: `${vendor.color || '#1a365d'}18` }}
          >
            <i className={`fas ${vendor.icon || 'fa-store'} fs-5`} style={{ color: vendor.color || '#1a365d' }}></i>
          </div>
          <div className="min-w-0">
            <h6 className="fw-bold mb-0">{vendor.name}</h6>
            {cat && (
              <span className="badge bg-light text-muted" style={{ fontSize: '0.7rem' }}>
                <i className={`fas ${cat.icon} me-1`}></i>{t(cat.labelKey)}
              </span>
            )}
          </div>
          {vendor.featured && (
            <span className="badge ms-auto" style={{ background: vendor.color || '#1a365d', fontSize: '0.65rem' }}>
              <i className="fas fa-star me-1"></i>{t('vendors.featured')}
            </span>
          )}
        </div>
        <p className="text-muted small mb-3">{vendor.description}</p>

        {/* Contact info */}
        {(vendor.contactEmail || vendor.contactPhone) && (
          <div className="mb-3 d-flex flex-column gap-1">
            {vendor.contactEmail && (
              <a href={`mailto:${vendor.contactEmail}`} className="text-muted small text-decoration-none d-flex align-items-center gap-1">
                <i className="fas fa-envelope" style={{ width: 14 }}></i>
                <span className="text-truncate">{vendor.contactEmail}</span>
              </a>
            )}
            {vendor.contactPhone && (
              <a href={`tel:${vendor.contactPhone}`} className="text-muted small text-decoration-none d-flex align-items-center gap-1">
                <i className="fas fa-phone" style={{ width: 14 }}></i>
                {vendor.contactPhone}
              </a>
            )}
          </div>
        )}

        <a
          href={vendor.href}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm rounded-pill px-3 w-100 fw-semibold"
          style={{ background: vendor.color || '#1a365d', color: '#fff', border: 'none' }}
        >
          <i className="fas fa-external-link-alt me-1"></i>{t('vendors.visit')}
        </a>
      </div>
    </div>
  );
}
